import { Grid } from '@material-ui/core';
import Typography from '@material-ui/core/Typography';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import FileCopy from '@material-ui/icons/FileCopy';
import React, { useEffect, useRef, useState } from 'react';
import { setAttendingsRequest } from '../../core/attending';
import { Auto } from '../../core/controls';
import { formatLocalDate } from '../../core/date-time';
import {
  Diagnosis,
  Procedure,
  Provider,
  Worksheet,
  WorksheetSummary as WSummary,
} from '../../core/domain';
import {
  Expansion,
  ExpansionDetails,
  ExpansionSummary,
} from '../../core/expansion';
import { FormApi, formApi, providerField } from '../../core/form';
import { personName, truncateSpan, withTooltip } from '../../core/format';
import { procedureSummary } from '../../core/procedure';
import { darkWhite, mediumGray, orange } from '../../core/theme';
import { showSuccessMessage, showToast } from '../../core/toast';
import { VisitContext, WorksheetApis } from '../../core/visit-context';
import { isReviewWorkflowState } from '../../core/workflow-api';
import { FieldValue, LinkButton } from '../common/presentation';

function CodeDesc({ code, desc, active }) {
  return (
    <Grid
      container
      item
      direction="row"
      alignItems="center"
      style={{
        width: '100%',
        flexWrap: 'nowrap',
        textDecoration: active ? undefined : 'line-through',
      }}
    >
      <Grid item>
        <Typography variant="button" display="block">
          {code + ' -\u00a0'}
        </Typography>
      </Grid>
      <Grid item style={{ fontStyle: 'italic' }}>
        {truncateSpan(desc, '232px')}
      </Grid>
    </Grid>
  );
}

function CptDesc({ cpt, active }) {
  const desc = procedureSummary(cpt, true, true);
  return (
    <Grid
      style={{
        width: '100%',
        textDecoration: active ? undefined : 'line-through',
      }}
      container
      item
      direction="row"
    >
      <Grid item>{truncateSpan(desc, '260px')}</Grid>
    </Grid>
  );
}

function Icd({ list, active }) {
  return (
    <Expansion
      defaultExpanded={true}
      style={{ width: '100%', paddingLeft: '6px' }}
      expanded={true}
    >
      <ExpansionSummary>
        <Typography color="textSecondary">ICD</Typography>
      </ExpansionSummary>
      <ExpansionDetails>
        <Grid
          style={{ width: '100%', paddingLeft: '6px' }}
          container
          direction="column"
        >
          {list.map((v, i) => (
            <CodeDesc
              key={i}
              code={v.diagnosisCode}
              desc={v.dxDescription}
              active={active || v.active}
            />
          ))}
        </Grid>
      </ExpansionDetails>
    </Expansion>
  );
}

function Cpt({ list, active }) {
  return (
    <Expansion
      defaultExpanded={true}
      style={{ width: '100%', paddingLeft: '6px' }}
      expanded={true}
    >
      <ExpansionSummary>
        <Typography color="textSecondary">CPT</Typography>
      </ExpansionSummary>
      <ExpansionDetails>
        <Grid
          style={{ width: '100%', paddingLeft: '6px' }}
          container
          direction="column"
        >
          {list.map((v, i) => (
            <CptDesc key={i} cpt={v} active={active || v.active} />
          ))}
        </Grid>
      </ExpansionDetails>
    </Expansion>
  );
}

type SummaryProps = {
  sheet: Worksheet;
  summary: WSummary;
  ctx: VisitContext;
  onAttendingChanged?: (v: Provider) => void;
  expanded?: boolean;
};
export function Summary({
  sheet,
  summary,
  ctx,
  onAttendingChanged,
  ...rest
}: SummaryProps) {
  const [expanded, setExpanded] = useState(rest.expanded);

  const onClick = () => {
    setExpanded(!expanded);
  };

  let physicians = [];
  if (ctx && ctx.config && ctx.config.primaryProviders) {
    physicians = ctx.config.primaryProviders;
  }

  const ownerForm: FormApi = useRef(
    formApi({
      disabled: ctx.readonly,
      values: { owner: sheet?.owningProvider },
      fields: {
        owner: providerField(physicians, { label: 'Worksheet Attending' }),
      },
      onChange: async (v) => {
        if (v.owner) {
          const data = { ...{ worksheetId: sheet.worksheetId }, ...v.owner };
          const result = await setAttendingsRequest(data);

          if (result.success) {
            if (onAttendingChanged) {
              onAttendingChanged(v.owner);
            }
            showSuccessMessage(
              personName(v.owner) + ' is now the worksheet attending.'
            );
          } else {
            showToast(
              'There was an error seting the attending:' +
                (result.errors || []).join('\n')
            );
          }
        }
      },
    })
  ).current;

  const {
    worksheetType,
    worksheetId,
    accountNumber,
    locationCode,
    departmentCode,
  } = sheet;

  const onCopy = (e: any) => {
    e.stopPropagation();
    window.navigator.clipboard.writeText(
      JSON.stringify({ locationCode, accountNumber, worksheetId })
    );
    showSuccessMessage('Copied to Clipboard');
  };

  let icds = sheet?.diagnoses.filter((v) => v.active);
  let cpts = sheet?.procedures.filter((v) => v.active);

  const showDeleted = isReviewWorkflowState();
  if (showDeleted) {
    icds = icds.concat(sheet?.diagnoses.filter((v) => !v.active));
    cpts = cpts.concat(sheet?.procedures.filter((v) => !v.active));
  }

  const workflowOwners = sheet?.workflowOwners || [];
  return (
    <Expansion key={sheet.worksheetType} expanded={expanded} onClick={onClick}>
      <ExpansionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography variant="subtitle2">{`${worksheetType} - ${departmentCode} - ${summary?.state}`}</Typography>
        {withTooltip(
          'Copy Worksheet Data to Clipboard',

          <FileCopy
            onClick={onCopy}
            htmlColor={mediumGray}
            style={{ fontSize: 24, paddingLeft: 12, paddingBottom: 8 }}
          />
        )}
      </ExpansionSummary>
      <ExpansionDetails
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
        }}
      >
        <Grid
          container
          direction="column"
          style={{
            width: '100%',
            maxHeight: '72vh',
            overflowX: 'hidden',
            overflowY: 'scroll',
            flexWrap: 'nowrap',
            paddingLeft: '6px',
            paddingBottom: '12px',
            backgroundColor: darkWhite,
          }}
        >
          <Grid item style={{ padding: 6 }}>
            <Auto {...ownerForm.controls.owner} />
          </Grid>

          {workflowOwners.length > 0 && (
            <Grid item style={{ padding: 6 }}>
              <FieldValue row={true} label="Workflow Owners" value={null} />
              {workflowOwners.map((v, i) => (
                <FieldValue
                  containerStyle={{ paddingLeft: 6 }}
                  row={true}
                  key={'wf' + i}
                  label={personName(v)}
                  value={v.workflow}
                />
              ))}
            </Grid>
          )}

          <Grid item style={{ padding: 6 }}>
            <FieldValue row={true} label="ID" value={worksheetId} />
          </Grid>

          <Grid item style={{ width: '100%' }}>
            <Icd list={icds} active={!showDeleted} />
          </Grid>
          <Grid item style={{ width: '100%' }}>
            <Cpt list={cpts} active={!showDeleted} />
          </Grid>
        </Grid>
      </ExpansionDetails>
    </Expansion>
  );
}

export function WorksheetSummary(props: VisitContext) {
  const { worksheets, visit } = props;
  const { locationCode, accountNumber, wsSummaries } = visit;
  const [list, setList] = useState(worksheets.map((v) => v.icdCpt.current()));

  useEffect(() => {
    worksheets.forEach((w) =>
      w.icdCpt.changed.subscribe(
        () => {
          setList(worksheets.map((v) => v.icdCpt.current()));
        },
        (s) => s.value
      )
    );
  }, []);

  const onAttendingChanged = (v: Provider) => {
    //TODO worksheet selection api
    const api = worksheets[0];
    if (api) {
      api.setPhysician(v);
    }
  };

  const { lastName, firstName } = visit.patient;

  return (
    <div
      style={{
        width: '100%',
        height: '83vh',
        overflowY: 'auto',
        overflowX: 'hidden',
        padding: '12px 24px 12px 24px',
      }}
    >
      <Grid container direction="column" style={{ width: '100%' }} spacing={2}>
        <Grid item>
          <FieldValue label="Patient" value={lastName + ', ' + firstName} />
        </Grid>

        <Grid
          item
          container
          direction="row"
          style={{ width: '100%' }}
          spacing={2}
        >
          <Grid item>
            <FieldValue label="Location Code" value={locationCode} />
          </Grid>
          <Grid item>
            <FieldValue label="Account Number" value={accountNumber} />
          </Grid>
        </Grid>
      </Grid>

      <br />
      {list.map((v, i) => (
        <Summary
          key={i}
          expanded={i === 0}
          sheet={v}
          summary={wsSummaries[i]}
          ctx={props}
          onAttendingChanged={onAttendingChanged}
        />
      ))}
    </div>
  );
}

type ElementLinkProps = {
  linkFn: (a: Diagnosis | Procedure) => string;
  onClick: (a: Diagnosis | Procedure) => void;
  el?: Diagnosis | Procedure;
};
function ElementLink(props: ElementLinkProps) {
  const { linkFn, onClick, el } = props;
  const link = linkFn(el);
  return (
    <Grid style={{ width: '100%' }} container item direction="row">
      <Grid item>
        <LinkButton
          label={link}
          color={orange}
          onClick={() => onClick(el)}
          width={260}
        />
      </Grid>
    </Grid>
  );
}
type ElementListProps = ElementLinkProps & {
  label: string;
  list: Diagnosis[] | Procedure[] | any;
};

function ElementList(props: ElementListProps) {
  const { label, list, linkFn, onClick } = props;

  return (
    <Expansion
      defaultExpanded={true}
      style={{ width: '100%', paddingLeft: '6px' }}
      expanded={true}
    >
      <ExpansionSummary>
        <Typography variant="body2" color="textSecondary">
          {label}
        </Typography>
      </ExpansionSummary>
      <ExpansionDetails>
        <Grid
          style={{ width: '100%', paddingLeft: '6px' }}
          container
          direction="column"
        >
          {list.map((v: any, i: number) => (
            <ElementLink key={i} el={v} linkFn={linkFn} onClick={onClick} />
          ))}
        </Grid>
      </ExpansionDetails>
    </Expansion>
  );
}

type WorksheetElementsProps = {
  api: WorksheetApis;
  worksheet: Worksheet;
};

function icdLink(a: Diagnosis) {
  return `${a.diagnosisCode} - ${a.dxDescription}`;
}

function cptLink(a: Procedure) {
  return `${procedureSummary(a, true, true)} - ${a.description}`;
}
function WorksheetElements({ api, worksheet }: WorksheetElementsProps) {
  const { icdCpt } = api;
  const {
    accountNumber,
    serviceDate,
    diagnoses,
    procedures,
    workflowOwners,
    departmentCode,
  } = worksheet;
  const [expanded, setExpanded] = useState(true);

  const title = `${accountNumber} - ${departmentCode} - ${formatLocalDate(
    serviceDate
  )}`;

  const onIcd = (a: Diagnosis) => {
    icdCpt.addIcd(a);
  };
  const onCpt = (a: Procedure) => {
    icdCpt.addCpt(
      {
        code: a.cptCode,
        description: { description: a.description },
      },
      true
    );
  };

  return (
    <Expansion
      key={accountNumber}
      expanded={expanded}
      onClick={() => setExpanded(!expanded)}
    >
      <ExpansionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography variant="subtitle2">{title}</Typography>
      </ExpansionSummary>
      <ExpansionDetails
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
        }}
      >
        <Grid
          container
          direction="column"
          style={{
            width: '100%',
            maxHeight: '72vh',
            overflowX: 'hidden',
            overflowY: 'scroll',
            flexWrap: 'nowrap',
            paddingLeft: '6px',
            paddingBottom: '12px',
            backgroundColor: darkWhite,
          }}
        >
          {workflowOwners?.length > 0 && (
            <Grid item style={{ padding: 6 }}>
              <FieldValue row={true} label="Workflow Owners" value={null} />
              {workflowOwners.map((v, i) => (
                <FieldValue
                  containerStyle={{ paddingLeft: 6 }}
                  row={true}
                  key={'wf' + i}
                  label={personName(v)}
                  value={v.workflow}
                />
              ))}
            </Grid>
          )}

          <Grid item style={{ width: '100%' }}>
            {!!diagnoses?.length && (
              <ElementList
                list={diagnoses}
                label="Diagnoses"
                linkFn={icdLink}
                onClick={onIcd}
              />
            )}
          </Grid>
          <Grid item style={{ width: '100%' }}>
            {!!procedures?.length && (
              <ElementList
                list={procedures}
                label="Procedures"
                linkFn={cptLink}
                onClick={onCpt}
              />
            )}
          </Grid>
        </Grid>
      </ExpansionDetails>
    </Expansion>
  );
}

export function PreviousVisits(props: VisitContext) {
  const { worksheets } = props;

  const list: [WorksheetApis, Worksheet][] = [];
  worksheets.forEach((v) => {
    const p = v.context.worksheet.pastWorksheets;
    p && p.forEach((u) => {
      list.push([v, u]);
    });
  });

  return (
    <div
      style={{
        width: '100%',
        height: '83vh',
        overflowY: 'auto',
        overflowX: 'hidden',
        padding: '12px 12px 12px 24px',
      }}
    >
      <Grid container direction="column" style={{ width: '100%' }} spacing={2}>
        <Grid item>PREVIOUS VISITS</Grid>
      </Grid>

      <br />
      {list.map((v, i) => (
        <WorksheetElements key={i} api={v[0]} worksheet={v[1]} />
      ))}
    </div>
  );
}
